/*
 * <delivery-check> — postcode gate for the product page.
 * Looks a postcode up in the free-delivery-postcodes asset (free = warehouse-to-depot
 * is in the price; anything else = quoted). Progressive enhancement: with JS off the
 * block still renders its label and input, it just does not resolve a result.
 * All copy comes from data-* attributes populated by locale strings in Liquid.
 */
class DeliveryCheck extends HTMLElement {
  constructor() {
    super();
    this.data = null;
    this.pending = null;
  }

  connectedCallback() {
    this.input = this.querySelector('[data-postcode]');
    this.button = this.querySelector('[data-check]');
    this.result = this.querySelector('[data-result]');
    if (!this.input || !this.button || !this.result) return;

    this.button.addEventListener('click', () => this.check());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); this.check(); }
    });
    // Re-checking after an edit clears the stale result.
    this.input.addEventListener('input', () => {
      // Keep the field to digits so the only thing a buyer can get wrong is the length.
      const digits = this.input.value.replace(/\D+/g, '').slice(0, 4);
      if (digits !== this.input.value) this.input.value = digits;
      if (!this.result.hidden) { this.result.hidden = true; this.result.innerHTML = ''; }
    });
  }

  async load() {
    if (this.data) return this.data;
    if (this.pending) return this.pending;
    const url = this.dataset.postcodesUrl;
    if (!url) return null;
    this.pending = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { this.data = j; return j; })
      .catch(() => null);
    return this.pending;
  }

  async check() {
    const raw = (this.input.value || '').trim();
    // Australian postcodes are four digits. A three digit entry used to be padded with a
    // leading zero; the merchant asked for the format to be enforced instead, so 0800 has to
    // be typed in full.
    if (!/^\d{4}$/.test(raw)) { this.render('invalid'); return; }
    const pc = raw;
    this.button.setAttribute('aria-busy', 'true');
    const data = await this.load();
    this.button.removeAttribute('aria-busy');
    if (!data || !data.free) { this.render('invalid'); return; }
    const depot = data.free[pc] || null;
    this.render(depot ? 'free' : 'paid', depot);
    // Let the page react (e.g. reveal the freight-quote form for quoted areas).
    this.dispatchEvent(new CustomEvent('delivery:result', {
      bubbles: true,
      detail: { postcode: pc, free: !!depot, depot: depot },
    }));
  }

  render(kind, depot) {
    const d = this.dataset;
    this.result.innerHTML = '';
    this.result.hidden = false;

    const box = document.createElement('div');
    if (kind === 'invalid') {
      box.className = 'delivery-check__callout delivery-check__callout--warn';
      box.textContent = d.invalid || '';
      this.result.appendChild(box);
      return;
    }

    const isFree = kind === 'free';
    box.className = 'delivery-check__callout delivery-check__callout--' + (isFree ? 'free' : 'paid');
    const heading = document.createElement('strong');
    heading.className = 'delivery-check__callout-heading';
    heading.textContent = isFree ? (d.freeHeading || '') : (d.paidHeading || '');
    const body = document.createElement('span');
    body.className = 'delivery-check__callout-body';
    body.textContent = isFree
      ? (d.freeBody || '').replace('[depot]', depot || '')
      : (d.paidBody || '');
    box.appendChild(heading);
    box.appendChild(body);
    this.result.appendChild(box);
  }
}

if (!customElements.get('delivery-check')) {
  customElements.define('delivery-check', DeliveryCheck);
}

/*
 * Carry the checked postcode into the enquiry forms so the buyer does not type it
 * twice. Only fills an empty field, never overwrites what the buyer typed.
 */
document.addEventListener('delivery:result', function (event) {
  const postcode = event.detail && event.detail.postcode;
  if (!postcode) return;
  document.querySelectorAll('input[data-delivery-postcode]').forEach(function (input) {
    if (input.value) return;
    input.value = postcode;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
});

/*
 * Out-of-zone reminder on the deposit and pay-in-full forms.
 *
 * Fires when the buyer types a postcode themselves that is not in a free depot zone and has
 * not ticked "Also add a freight quote to my invoice". It warns and nothing more: someone
 * collecting from the warehouse is a legitimate case, so submission is never blocked and the
 * tick box is never set for them.
 *
 * A postcode carried down from the delivery checker does not trigger it, because that buyer
 * has already seen the same message in the checker's own callout. The prefill above dispatches
 * a synthetic input event, so event.isTrusted separates a keystroke from a prefill.
 *
 * A failed or missing postcode file stays silent rather than warning on a guess.
 */
(function () {
  let zones = null;
  let pending = null;

  function loadZones(url) {
    if (zones) return Promise.resolve(zones);
    if (pending) return pending;
    if (!url) return Promise.resolve(null);
    pending = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { zones = j; return j; })
      .catch(function () { return null; });
    return pending;
  }

  function setup(notice) {
    // The script can be requested by more than one block on the page; bind each notice once.
    if (notice.dataset.noticeReady) return;
    const form = notice.closest('form');
    if (!form) return;
    notice.dataset.noticeReady = 'true';
    const input = form.querySelector('input[data-delivery-postcode]');
    const tick = form.querySelector('input[name="contact[Freight quote requested]"]');
    if (!input || !tick) return;

    let typed = false;

    function hide() {
      notice.hidden = true;
    }

    function update() {
      if (!typed || tick.checked) { hide(); return; }
      const pc = (input.value || '').trim();
      if (!/^\d{4}$/.test(pc)) { hide(); return; }
      loadZones(notice.dataset.postcodesUrl).then(function (data) {
        // Guard against a slow fetch resolving after the buyer has moved on.
        if ((input.value || '').trim() !== pc || tick.checked || !typed) { hide(); return; }
        if (!data || !data.free) { hide(); return; }
        notice.hidden = !!data.free[pc];
      });
    }

    input.addEventListener('input', function (event) {
      if (event.isTrusted) typed = true;
      // Keep the field to digits, same rule as the checker.
      const digits = input.value.replace(/\D+/g, '').slice(0, 4);
      if (digits !== input.value) input.value = digits;
      update();
    });
    tick.addEventListener('change', update);
  }

  function init() {
    document.querySelectorAll('[data-postcode-notice]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

